import { ApiInstance, unscoped } from '@/lib/api'
import type { SessionUser } from '@/lib/session'

export type PermissionLevel = 'read' | 'write' | 'delete'

const LEVEL_RANK: Record<string, number> = { read: 1, write: 2, delete: 3 }

export interface MembershipPermission {
  resource: string
  level: PermissionLevel
}

export interface AccessCompanyRef {
  id: string
  name: string
  isActive: boolean
}

export interface AccessMembership {
  id: string
  company: AccessCompanyRef
  role: string
  permissions: MembershipPermission[]
  subscriptionActive: boolean
}

export interface MyAccess {
  userId: string
  isSuperAdmin: boolean
  memberships: AccessMembership[]
}

export type SessionTarget =
  | { kind: 'membership'; company: AccessCompanyRef }
  | { kind: 'adminCompany'; companyId: string }
  | { kind: 'adminHome' }
  | { kind: 'onboarding' }

export const ADMIN_HOME = '/administracao/imobiliarias'

export interface AuthSession {
  status?: 'authenticated'
  accessToken: string
  expiresAt: string
  user: SessionUser
  redirectTo?: string | null
}

export async function fetchMyAccess(): Promise<MyAccess> {
  const { data } = await ApiInstance.get<MyAccess>('/me/access', unscoped())
  return data
}

export function findMembership(access: MyAccess | undefined | null, companyId?: string | null): AccessMembership | undefined {
  if (!access?.memberships?.length) return undefined
  if (companyId) {
    const match = access.memberships.find(membership => membership.company.id === companyId)
    if (match) return match
  }
  return access.memberships[0]
}

export function membershipOf(access: MyAccess | undefined | null, companyId?: string | null): AccessMembership | undefined {
  if (!companyId) return undefined
  return access?.memberships?.find(membership => membership.company.id === companyId)
}

const TRIAL_RETRY_DELAYS_MS = [500, 1000, 1500, 2000, 2500, 2500]

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export async function fetchAccessWithTrial(companyId: string, wait = sleep): Promise<MyAccess> {
  let access = await fetchMyAccess()
  for (const delay of TRIAL_RETRY_DELAYS_MS) {
    if (membershipOf(access, companyId)?.subscriptionActive) return access
    await wait(delay)
    access = await fetchMyAccess()
  }
  return access
}

export function resolveSessionTarget(access: MyAccess, companyId?: string | null): SessionTarget {
  const exact = membershipOf(access, companyId)
  if (exact) return { kind: 'membership', company: exact.company }
  if (access.isSuperAdmin && companyId) return { kind: 'adminCompany', companyId }
  const first = access.memberships?.[0]
  if (first) return { kind: 'membership', company: first.company }
  return access.isSuperAdmin ? { kind: 'adminHome' } : { kind: 'onboarding' }
}

export function grants(permissions: MembershipPermission[] | undefined, resource: string, level?: string): boolean {
  if (!resource) return false
  const required = level ? (LEVEL_RANK[level] ?? Number.POSITIVE_INFINITY) : LEVEL_RANK.read
  const best = (permissions ?? []).reduce((acc, permission) => {
    if (permission.resource !== resource) return acc
    return Math.max(acc, LEVEL_RANK[permission.level] ?? 0)
  }, 0)
  return best >= required
}

export function companiesOf(access: MyAccess | undefined | null): AccessCompanyRef[] {
  return (access?.memberships ?? []).map(membership => membership.company)
}
